import { withNamespaces } from "react-i18next";
import { useEffect, useMemo } from "react";
import { withRouter } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { QuestionnaireDesigner } from "@smartintegrity/questionnaire";

import "./style.scss";

import QuestionnaireDesignerService from 'src/modules/data-protection/apis/QuestionnaireDesignerService';
import { categoryCustomQuestionType } from "../components/CategoryQuestionDesigner/registration";
import {
  gdprQuestionnaireToDesignerModel,
  mapDesignerOptionToGdpr,
  // designerModelToGdprQuestionnaire,
} from "../../../../../helpers/questionnaireDesignerAdapters";
import { getGdprSystemDataDeleteConfirmation } from "../../../../../helpers/systemDataDeletion";

/**
 * GDPR-supported Designer question types.
 *
 * Keys are the types offered when creating questions. Only radiobox options
 * can hold sub-questions, limited to GDPR_SUB_QUESTION_TYPES.
 * Country list / country multi-select mirror runtime `extraData.countries`
 * (no authored options). Multi select uses authored options like `select`.
 * Types without shared-Designer / custom-Designer support are listed in
 * UNSUPPORTED_GDPR_QUESTION_TYPES and are not offered for creation.
 */
const GDPR_SUB_QUESTION_TYPES = [
  "text",
  "textarea",
  "date",
  "file_upload",
  "multi_select",
  "country_multi_select",
  "country_list",
];

const GDPR_DESIGNER_QUESTION_TYPES = {
  text: { children: false },
  textarea: { children: false },
  date: { children: false },
  radiobox: {
    children: {
      enabled: true,
      questionTypes: GDPR_SUB_QUESTION_TYPES,
    },
  },
  checkbox: { children: false },
  select: {
    children: {
      enabled: false
    },
  },
  multi_select: { children: false },
  country_list: { children: false },
  country_multi_select: { children: false },
  legal_entity: { children: false },
  file_upload: { children: false },
  category: { children: false, max: 1 },
};

/**
 * Not creatable in the shared Designer until dedicated custom question type
 * extensions exist. Existing questions of these types still load via adapters.
 */
export const UNSUPPORTED_GDPR_QUESTION_TYPES = [
  "switch",
];

const GDPR_DESIGNER_FEATURES = {
  // GDPR questionnaires are one flat question list: no Questions Tabs, no panels.
  pageManagement: false,
  pageIconSelector: false,
  sectionManagement: false,
  questionTypes: GDPR_DESIGNER_QUESTION_TYPES,
  // GDPR never used conditional visibility in the old Designer.
  conditionalRules: false,
  // Pages/panels are section groupings without backend IDs or delete APIs.
  deletion: {
    page: false,
    panel: false,
    question: true,
    option: true,
  },
};

const GDPR_CUSTOM_QUESTION_TYPES = {
  category: categoryCustomQuestionType,
};

const DataProtectionEditQuestionnaire = (props) => {
  const { t, selectedQuestionaire } = props;

  const handleFetchQuestionaireDetails = useQuery({
    queryKey: [
      "data-protection-admin-questionaire-details",
      selectedQuestionaire?.id,
    ],
    queryFn: async () => {
      const service = QuestionnaireDesignerService.getInstance();

      return await service.questionnaireDetail(selectedQuestionaire.id);
    },
    cacheTime: 0,
    enabled: false,
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while fetching questionnaire detail."), {
        type: "error",
      });
    },
  });

  const {
    mutate: updateQuestionnaire,
    isLoading: updatingQuestionnaire,
  } = useMutation({
    mutationFn: async (payload) => {
      const service = QuestionnaireDesignerService.getInstance();

      return await service.updateQuestionnaire(payload);
    },
    onSuccess: () => {
      toast(t("Questions list updated successfully."), {
        type: "success",
      });

      handleFetchQuestionaireDetails.refetch();
    },
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while updating the questions list."), {
        type: "error",
      });
    },
  });

  const { mutateAsync: deleteQuestion } = useMutation({
    mutationFn: async (questionId) => {
      const service = QuestionnaireDesignerService.getInstance();

      return await service.deleteQuestion(questionId);
    },
    onSuccess: () => {
      toast(t("Question deleted successfully."), {
        type: "success",
      });
    },
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while deleting the question."), {
        type: "error",
      });
    },
  });

  const { mutateAsync: deleteQuestionOption } = useMutation({
    mutationFn: async ({ optionId, payload = {} }) => {
      const service = QuestionnaireDesignerService.getInstance();

      return await service.deleteQuestionOption(optionId, payload);
    },
    onSuccess: () => {
      toast(t("Option deleted successfully."), {
        type: "success",
      });
    },
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while deleting the option."), {
        type: "error",
      });
    },
  });

  useEffect(() => {
    if (selectedQuestionaire) {
      handleFetchQuestionaireDetails.refetch();
    }
  }, [selectedQuestionaire]);

  // Depends on the fetched data only (same as the TPDD consumer): a new
  // defaultModel reference makes the shared Designer re-initialize, which would
  // discard in-progress edits.
  const defaultModel = useMemo(() => {
    if (!handleFetchQuestionaireDetails?.data) {
      return [];
    }

    return gdprQuestionnaireToDesignerModel(
      handleFetchQuestionaireDetails.data,
      { t }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleFetchQuestionaireDetails.data]);

  const questionnaireBasicData = useMemo(() => {
    if (!selectedQuestionaire) {
      return undefined;
    }

    return {
      title: selectedQuestionaire.title,
      id: selectedQuestionaire.id,
    };
  }, [selectedQuestionaire]);

  const handleSaveQuestionnaire = ({ elements }) => {
    if (!selectedQuestionaire?.id) {
      return;
    }

    // Designer -> GDPR adapter is bypassed on save; the Designer model is sent
    // straight to the backend using the same mapping as the TPDD module.
    // const payload = designerModelToGdprQuestionnaire(elements, {
    //   questionnaireId: selectedQuestionaire.id,
    //   includeSections: false,
    // });

    const result = {
      questionnaire: selectedQuestionaire.id,
      questions: [],
    };

    let questionSort = 0;

    elements.forEach((page) => {
      const { elements: pageElements } = page;

      (pageElements || []).forEach((element) => {
        const questions =
          element.elementType === "question" ? [element] : element.elements || [];

        questions.forEach((question) => {
          const { options } = question;
          const isCategoryQuestion = question.questionType === "category";

          // Category options carry reportingDeadlineHours (and `custom`
          // resolution), which the TPDD option mapping drops, so they keep going
          // through the GDPR adapter.
          const mappedOptions = !options?.length
            ? undefined
            : isCategoryQuestion
            ? options.map((option) => {
                return mapDesignerOptionToGdpr(option, {
                  optionTypeFallback: "checkbox",
                });
              })
            : options.map((option) => {
                const optionsChildren = (option?.children || []).map(
                  (optionChild) => {
                    return {
                      // Persisted items must keep their backend id, otherwise
                      // the save API recreates them as duplicates.
                      id: optionChild.dbID,
                      title: optionChild.title,
                      type: optionChild.questionType,
                      children: (optionChild?.options || []).map(
                        (childOption) => {
                          return {
                            id: childOption.dbID,
                            title: childOption.title,
                            type: optionChild.questionType,
                          };
                        }
                      ),
                    };
                  }
                );

                return {
                  id: option.dbID,
                  type: question.questionType,
                  title: option.title,
                  children: !optionsChildren.length
                    ? undefined
                    : optionsChildren,
                };
              });

          result.questions.push({
            id: question.dbID,
            title: question.title,
            sortIndex: questionSort++,
            isMandatory: !!question.isMandatory,
            type: question.questionType,
            options: mappedOptions,
          });
        });
      });
    });

    updateQuestionnaire(result);
  };

  /**
   * Customize the shared Designer's single confirmation modal for questions
   * linked via system_data. Returns null for default Designer copy.
   * Confirmation is shown by the Designer — not a second GDPR modal.
   */
  const handleGetDeleteConfirmation = (payload) => {
    return getGdprSystemDataDeleteConfirmation(payload);
  };

  const handleDeleteQuestionnaireItem = async ({ type, id, context }) => {
    // `id` is the backend dbID selected by the shared Designer. Returning the
    // mutateAsync promise lets the Designer remove the item only after success
    // and retain it when the API rejects.
    //
    // The Designer reports option sub-questions as `question` too, but GDPR
    // stores them as option children, so they use the option endpoint.
    //
    // system_data warnings are handled via getDeleteConfirmation — not here —
    // so only one confirmation modal is shown.
    const isOptionChild = type === "question" && context?.childIndex != null;

    if (type === "question" && !isOptionChild) {
      return deleteQuestion(id);
    }

    if (type === "option" || isOptionChild) {
      const parentQuestion = context?.question || context?.parentQuestion;
      const payload =
        parentQuestion?.questionType === "category"
          ? { questionType: "category" }
          : {};

      return deleteQuestionOption({ optionId: id, payload });
    }

    return Promise.reject(
      new Error(`Unsupported GDPR questionnaire deletion type: ${type}`)
    );
  };

  const isLoading =
    !selectedQuestionaire ||
    handleFetchQuestionaireDetails.isLoading ||
    handleFetchQuestionaireDetails.isFetching;

  if (isLoading) {
    return <div className="mt-2">{t("Loading")}...</div>;
  }

  return (
    <QuestionnaireDesigner
      isLoading={false}
      savingInprocess={updatingQuestionnaire}
      onSaveButtonClicked={handleSaveQuestionnaire}
      getDeleteConfirmation={handleGetDeleteConfirmation}
      onDelete={handleDeleteQuestionnaireItem}
      questionnaireBasicData={questionnaireBasicData}
      defaultModel={defaultModel}
      features={GDPR_DESIGNER_FEATURES}
      customQuestionTypes={GDPR_CUSTOM_QUESTION_TYPES}
    />
  );
};

export default withNamespaces()(
  withRouter(DataProtectionEditQuestionnaire)
);
